import React, { useContext, useState } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import cx from 'classnames';

import EditableText from 'components/shared/EditableText';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import MapPoints from 'components/shared/MapBlock/MapPoints';
import { ITINERARIES_ITEM_TYPES } from 'constants/index';
import i18n from 'i18n';
import { getDateFormat } from 'utils/date';
import { formatTimeRangeAdapter } from 'utils/itineraryDetails';

import s from './index.module.css';

export const ItineraryItem = (props) => {
  return (
    <div className={cx(s.item, 'custom-overview-map-list-item')} data-block>
      <span className="overviewMapTop custom-overview-map-list-item-top">
        <strong className={cx(s.itemCount, 'custom-overview-map-list-item-count')}>{`${props?.index + 1}) `}</strong>{' '}
        <span>
          {props?.timeRange && (
            <>
              {formatTimeRangeAdapter(
                props?.scheduleType,
                { start: props?.timeRange?.[0], end: props?.timeRange?.[1] },
                {
                  includeOnlyDifferentYears: true,
                  translate: true,
                  translationFormat: '%B %-d',
                  translationDateFormat: props.dateFormat,
                },
              )}
              {': '}
            </>
          )}
        </span>
      </span>
      <span className="overviewMapBottom custom-overview-map-list-item-bottom">
        {props?.propertyName}
        {props?.areaName && ', '}
        {props?.areaName}
      </span>
    </div>
  );
};

const LookbookItem = (props) => {
  return (
    <div className={cx(s.item, 'custom-overview-map-list-item')} data-block>
      <strong className={cx(s.itemCount, 'custom-overview-map-list-item-count')}>{`${props?.index + 1}) `}</strong>{' '}
      {props?.propertyName ? (
        <span className="overviewMapBottom custom-overview-map-list-item-bottom">
          {props?.propertyName}
          {props?.areaName && ', '}
          {props?.areaName}
        </span>
      ) : (
        <span className="overviewMapBottom custom-overview-map-list-item-bottom">
          {props?.areaName}
          {props?.countryName && ', '}
          {props?.countryName}
        </span>
      )}
    </div>
  );
};

export const List = ({ collection, scheduleType, isLookbook, dateFormat }) => {
  if (collection?.length > 0) {
    return collection.map((item, i) =>
      isLookbook ? (
        <LookbookItem key={i} index={i} {...item} />
      ) : (
        <ItineraryItem key={i} index={i} {...item} scheduleType={scheduleType} dateFormat={dateFormat} />
      ),
    );
  }
  return (
    <div className={cx(s.item, 'custom-overview-map-list-item')} data-block>
      {i18n.t('proposal_content.no_data')}
    </div>
  );
};

function OverviewMap(props) {
  const { data, onUpdate } = props;
  const { readOnly, page, itinerariesItemType } = useContext(LandingPageContext);
  const [isInViewport, setIsInViewport] = useState(false);
  const collection = page?.way;
  const scheduleType = page?.scheduleType;
  const title = data?.data?.title;
  const points = collection?.map((item) => ({
    lat: Number(item?.latitude),
    lng: Number(item?.longitude),
    propertyName: item?.propertyName,
  }));
  const mapContainerStyle = { width: '100%', height: '319px' };

  const handleChange = (enteredValue, type) => {
    const obj = { ...data, data: { ...data?.data, [type]: enteredValue } };
    onUpdate(obj);
  };

  const handleIntersection = (event, unobserve) => {
    if (event.isIntersecting) {
      setIsInViewport(true);
      unobserve();
    }
  };

  return (
    <Observer onChange={handleIntersection}>
      <div className={cx(s.wrapper, 'custom-overview-map-wrapper')}>
        <div className={cx(s.map, 'custom-overview-map')} style={mapContainerStyle}>
          {!points || points?.length === 0 ? (
            <div className={s.mapPlaceholder} data-block>
              No Coords
            </div>
          ) : (
            isInViewport && <MapPoints containerStyle={mapContainerStyle} points={points} />
          )}
        </div>
        <div className={cx(s.list, 'custom-overview-map-list')}>
          <div className={cx(s.listTitle, 'custom-overview-map-list-title')}>
            <EditableText
              text={title}
              onChange={(value) => handleChange(value, 'title')}
              placeholder="Title"
              textAlignment="center"
              isReadonly={readOnly}
            />
          </div>
          <div id="overviewMapList">
            <List
              collection={collection}
              scheduleType={scheduleType}
              isLookbook={itinerariesItemType === ITINERARIES_ITEM_TYPES.LOOKBOOK}
              dateFormat={getDateFormat(page?.settings?.displayDatesDdMmYyyy)}
            />
          </div>
        </div>
      </div>
    </Observer>
  );
}

export default OverviewMap;
